import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { GradeBadge, StatusBadge } from "@/components/common/Primitives";
import { Printer, ArrowLeft } from "lucide-react";

export default function ReportViewPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/reports/pdf/${id}`).then(({ data }) => setData(data)); }, [id]);

  if (!data) return <div className="text-slate-500">Loading report...</div>;

  const catMap = Object.fromEntries((data.categories || []).map((c) => [c.id, c]));
  const questionMap = Object.fromEntries((data.questions || []).map((q) => [q.id, q]));
  const answersByCat = {};
  (data.answers || []).forEach((a) => {
    const q = questionMap[a.question_id];
    if (!q) return;
    answersByCat[q.category_id] = answersByCat[q.category_id] || [];
    answersByCat[q.category_id].push({ ...a, question: q });
  });

  return (
    <div className="space-y-4">
      <div className="no-print flex items-center justify-between">
        <button onClick={() => nav(-1)} className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1">
          <ArrowLeft size={12} /> Back
        </button>
        <Button onClick={() => window.print()} className="bg-sams-primary hover:bg-sams-primaryHover text-white" data-testid="print-report-button">
          <Printer size={14} className="mr-2" /> Print / Save PDF
        </Button>
      </div>

      <div className="print-page bg-white border border-slate-200 rounded-md p-8 space-y-6 max-w-4xl mx-auto">
        <div className="border-b-4 border-sams-primary pb-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-bold">Supplier Audit Report</div>
              <h1 className="font-heading text-4xl font-black text-slate-900 mt-1">{data.audit_number}</h1>
              <div className="text-sm text-slate-600 mt-1">Fiscal Year {data.audit_month?.slice(0, 4)} · {data.audit_type}</div>
            </div>
            <div className="text-right">
              <StatusBadge status={data.status} />
              <div className="mt-2"><GradeBadge grade={data.grade} /></div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Supplier</div>
            <div className="font-semibold text-slate-900 text-base">{data.supplier?.name}</div>
            <div className="text-slate-600 text-xs">{data.supplier?.code} · {data.supplier?.category}</div>
            <div className="text-slate-600 text-xs">{data.supplier?.address}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Plant / Site</div>
            <div className="font-semibold text-slate-900 text-base">{data.plant?.name || "—"}</div>
            <div className="text-slate-600 text-xs">{data.plant?.location || ""}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Planned Date</div>
            <div className="font-semibold text-slate-900">{data.planned_date}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Auditor(s)</div>
            <div className="font-semibold text-slate-900">{(data.auditor_details || []).map((u) => u.name).join(", ") || "—"}</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="border border-slate-200 rounded-md p-4 bg-slate-50">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Total Score</div>
            <div className="font-heading text-3xl font-black text-slate-900 mt-1">{data.percentage}%</div>
            <div className="text-xs text-slate-500">{data.total_score} / {data.max_score}</div>
          </div>
          <div className="border border-slate-200 rounded-md p-4 bg-slate-50">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Grade</div>
            <div className="mt-2"><GradeBadge grade={data.grade} /></div>
          </div>
          <div className="border border-slate-200 rounded-md p-4 bg-slate-50">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Findings</div>
            <div className="font-heading text-3xl font-black text-slate-900 mt-1">{(data.findings || []).length}</div>
          </div>
        </div>

        {(data.categories || []).map((c) => {
          const answers = answersByCat[c.id] || [];
          if (answers.length === 0) return null;
          return (
            <div key={c.id}>
              <h3 className="font-heading font-bold text-lg text-slate-900 mt-4 mb-2">{c.name}</h3>
              <table className="w-full text-xs border border-slate-200">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="text-left px-2 py-1.5 border-r border-slate-200">#</th>
                    <th className="text-left px-2 py-1.5 border-r border-slate-200">Question</th>
                    <th className="text-right px-2 py-1.5 border-r border-slate-200">Score</th>
                    <th className="text-left px-2 py-1.5">Remark</th>
                  </tr>
                </thead>
                <tbody>
                  {answers.map((a, i) => (
                    <tr key={a.id} className="border-t border-slate-200">
                      <td className="px-2 py-1.5 border-r border-slate-200 font-mono">{i + 1}</td>
                      <td className="px-2 py-1.5 border-r border-slate-200">{a.question.question}</td>
                      <td className="px-2 py-1.5 border-r border-slate-200 text-right font-mono font-semibold">{a.score}/{a.question.max_score}</td>
                      <td className="px-2 py-1.5">{a.remark || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}

        {(data.findings || []).length > 0 && (
          <div>
            <h3 className="font-heading font-bold text-lg text-slate-900 mt-4 mb-2">Findings</h3>
            <div className="space-y-2">
              {data.findings.map((f, i) => (
                <div key={f.id} className="border border-slate-200 rounded-md p-3 text-sm">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Finding #{i + 1} · Severity: {f.severity}</div>
                  <div className="font-medium text-slate-900 mt-1">{f.description}</div>
                  {f.recommendation && <div className="text-xs text-slate-600 mt-1"><b>Recommendation:</b> {f.recommendation}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {(data.capas || []).length > 0 && (
          <div>
            <h3 className="font-heading font-bold text-lg text-slate-900 mt-4 mb-2">CAPA Actions</h3>
            <table className="w-full text-xs border border-slate-200">
              <thead><tr className="bg-slate-100"><th className="text-left px-2 py-1.5">Description</th><th className="text-left px-2 py-1.5">Due</th><th className="text-left px-2 py-1.5">Status</th></tr></thead>
              <tbody>
                {data.capas.map((c) => (
                  <tr key={c.id} className="border-t border-slate-200">
                    <td className="px-2 py-1.5">{c.description}</td>
                    <td className="px-2 py-1.5 font-mono">{c.due_date}</td>
                    <td className="px-2 py-1.5"><StatusBadge status={c.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="pt-6 border-t border-slate-200 grid grid-cols-2 gap-8 text-xs">
          <div>
            <div className="border-t border-slate-400 pt-1 mt-8">
              <div className="font-semibold text-slate-900">Auditor Signature</div>
              <div className="text-slate-500">{(data.auditor_details || [])[0]?.name || ""}</div>
            </div>
          </div>
          <div>
            <div className="border-t border-slate-400 pt-1 mt-8">
              <div className="font-semibold text-slate-900">Manager Signature</div>
              <div className="text-slate-500">Approved on {(data.reviews || [])[0]?.timestamp ? new Date(data.reviews[0].timestamp).toLocaleDateString() : "—"}</div>
            </div>
          </div>
        </div>
        <div className="text-[10px] text-slate-400 text-center pt-4">Generated by SAMS · Confidential</div>
      </div>
    </div>
  );
}
